import * as React from "react";
import { Component, KeyboardEvent } from "react";
import { AuditContext, AuditContextValue } from "~/common/audit";
import { trackEvent } from "~/common/audit";
import { Keys } from "~/common/enums";
import { memoizeOne } from "~/EasyProjectsCommon/memoizeOne";
import { UnionAppEntity } from "~/normalizr/entities";
import { BlockForFullStory, blockForFullStory } from "~/utils/blockForFullStory";
import { Button } from "../buttons/Button";
import Loading from "../loading/Loading";
import { ItemAdditionalOptions, VirtListRowProps } from "./BaseList";
import { NoItemsMessage } from "./NoItemsMessage";
import { PlainList } from "./PlainList";
import { ItemKey } from "./types";
import { emptyGroup, getFocused, isSelectable } from "./utils/listUtils";
import { VirtList } from "./VirtList";

enum focusItemDirection {
    first = "first",
    up = "up",
    down = "down",
    pageup = "pageup",
    pagedown = "pagedown",
    last = "last",
}

type ItemBaseType<ItemType> = ItemType & ItemAdditionalOptions<ItemType>;

export type AdditionalRowOptions<ItemType> = {
    isAvailable?: boolean;
    showAtTheEndOnSearch?: boolean;
    showOnSearch?: boolean;
    hideOnExactMatch?: boolean;
    showAtTheEnd?: boolean;
    addAction?: (item?: ItemType, callback?: any) => void;
};

export interface AdditionalRow<ItemType> {
    component?: React.SFC<VirtListRowProps<ItemType>>;
    options: AdditionalRowOptions<ItemType>;
}

export interface ListBaseProps<TItem> extends BlockForFullStory {
    items: ItemBaseType<TItem>[];
    getItemLabel?: (item: TItem) => string;
    getItemSelected?: (item: TItem) => boolean;
    getItemType?: (item: TItem) => UnionAppEntity;
    itemHeight?: number;
    getItemHeight?: (item: TItem, index: number, items: TItem[]) => number;
    getItemClassName?: (item: TItem, items: TItem[]) => string;
    /** If returns true, the item will not be [un]selected on click or enter */
    isItemDisabled?: (item: TItem) => boolean;
    disabledItemTooltip?: string;
    listRowComponent?: React.SFC<VirtListRowProps<TItem>>;
    additionalRows?: AdditionalRow<TItem>[];
    isCreating?: boolean;
    searchText?: string;
    listWidth?: number;
    onDelete?: (item: TItem) => void;
    hideItem?: F1<TItem, boolean>;
    ignoreHomeEndNavigation?: boolean;
    customScrollbarWrapperClassName?: string;
    getItemKeyForRender?: (item: TItem) => any;
    getItemKey?: (item: TItem) => ItemKey;
    customScrollbar?: boolean;
    withAutoRowItemHeight?: boolean;
    isExactMatch?: (item: TItem, filterString: string) => boolean;
}

export interface ListProps<ItemType> extends ListBaseProps<ItemType> {
    pageSize?: number;
    selectedValue?: ItemType;
    onSelectItem: (item: ItemType, event?: React.KeyboardEvent) => void;
    /**
     * Custom filter function to manual search control.
     * Usually used to find item by specific criteria, e.g. email for users, id for task or project, etc.
     */
    filterItem?: (item: ItemType, searchText: string) => boolean;
    isLoading?: boolean;
    isVirtualized?: boolean;
    listMaxWidth?: number;
    /**
     * if specified then list might be focused (or navigated by tab)
     * and also listens to keydown itself (selection and navigation by items working out of the box)
     */
    tabIndex?: number;
    customScrollbar?: boolean;
    hideNoItem?: boolean;
    noItemsClassName?: string;
    noItemsText?: string;
    forceRender?: any;
    auditEvent?: string;
}

interface ListState<ItemType> {
    filteredItems: ItemType[];
    focusedItem: ItemBaseType<ItemType>;
    initialFocusedItem: ItemType;
}

const defaultProps = {
    pageSize: 5,
    itemHeight: 34,
    isLoading: false,
    isVirtualized: true,
};

type GetFilteredItems<T> = (
    items: T[],
    filterText: string,
    hideItem: F1<T, boolean>,
    additionalRows: AdditionalRow<T>[],
    isExactMatch: F2<T, string, boolean>
) => T[];

export class List<ItemType> extends Component<
    ListProps<ItemType>,
    ListState<ItemType & ItemAdditionalOptions<ItemType>>
> {
    static defaultProps = defaultProps;
    virtListRef: React.RefObject<VirtList<ItemType>> = React.createRef();
    _recomputeStyles = false;
    context!: AuditContextValue;

    constructor(props: ListProps<ItemType>) {
        super(props);
        const focusedItem = props.selectedValue
            ? props.selectedValue
            : props.items && props.items.length
            ? props.items[0]
            : null;

        this.state = {
            filteredItems: this.getFilteredItemsMemo(
                props.items,
                props.searchText,
                props.hideItem,
                props.additionalRows,
                props.isExactMatch
            ),
            focusedItem,
            initialFocusedItem: focusedItem,
        };

        this.onKeyDown = this.onKeyDown.bind(this);
        this.onSelectItem = this.onSelectItem.bind(this);
        this.setFocusedItem = this.setFocusedItem.bind(this);
    }

    static getDerivedStateFromProps<ItemType>(nextProps: ListProps<ItemType>, prevState: ListState<ItemType>) {
        let newState = null;
        if (prevState.initialFocusedItem !== nextProps.selectedValue) {
            newState = {
                focusedItem: nextProps.selectedValue,
                initialFocusedItem: nextProps.selectedValue,
            } as Partial<ListState<ItemType>>;
        }

        return newState;
    }

    getNewItem = memoizeOne(
        (
            text: string,
            addAction: (item: ItemType) => void,
            addRowComponent: React.SFC<VirtListRowProps<ItemType>>
        ): ItemAdditionalOptions<ItemType> => ({
            addRow: true,
            addAction,
            addRowComponent,
            newValue: text,
        })
    );

    getFilteredItems: GetFilteredItems<ItemType> = (
        items,
        rawFilterText,
        hideSelectedItem,
        additionalRows,
        isExactMatch
    ) => {
        const filterText = rawFilterText?.trim();
        const needFiltering = filterText || hideSelectedItem;

        const filteredItems = needFiltering
            ? items.filter(item => this.filterItem(item, filterText)).filter(emptyGroup)
            : items;

        if (!additionalRows) {
            return filteredItems;
        }

        const hasExactMatch =
            filterText &&
            items?.find(x =>
                !!isExactMatch
                    ? isExactMatch(x, filterText)
                    : this.props.getItemLabel?.(x)?.toLowerCase() === filterText?.toLowerCase()
            );

        const isAvailableAdditionalItem = (i: AdditionalRow<ItemType>) => i.options.isAvailable;
        const isVisibleByExactMatch = (i: AdditionalRow<ItemType>) => {
            const { hideOnExactMatch } = i.options;
            return !hideOnExactMatch || (hideOnExactMatch && !hasExactMatch);
        };

        const isVisibleBySearch = (i: AdditionalRow<ItemType>) => {
            const { showOnSearch } = i.options;
            return !showOnSearch || (showOnSearch && filterText);
        };

        const visibleAdditionalItems = additionalRows.filter(
            i => isAvailableAdditionalItem(i) && isVisibleByExactMatch(i) && isVisibleBySearch(i)
        );

        if (!visibleAdditionalItems.length) {
            return filteredItems;
        }

        const createNewRowItems = (additionalRows: AdditionalRow<ItemType>[]) =>
            additionalRows.map(x => {
                const { addAction } = x.options ?? {};

                return this.getNewItem(filterText, addAction, x.component) as ItemType;
            });

        const isShowAtTheEndOnSearch = (i: AdditionalRow<ItemType>) =>
            (i.options.showAtTheEndOnSearch && filterText) || i.options.showAtTheEnd;

        const staticAdditionalItems = visibleAdditionalItems.filter(i => !isShowAtTheEndOnSearch(i));
        const dynamicAdditionalItems = visibleAdditionalItems.filter(isShowAtTheEndOnSearch);

        return [
            ...createNewRowItems(staticAdditionalItems),
            ...filteredItems,
            ...createNewRowItems(dynamicAdditionalItems),
        ];
    };

    getFilteredItemsMemo = memoizeOne(this.getFilteredItems);

    filterItem(item: ItemType, inputValue: string) {
        const { filterItem, getItemLabel, hideItem } = this.props;

        if (hideItem && hideItem(item)) {
            return false;
        }

        if (filterItem) {
            return filterItem(item, inputValue);
        }

        if (!inputValue || !isSelectable(item)) {
            return true;
        }

        let label = getItemLabel(item) || "";
        label = label.toLowerCase();
        return label.includes(inputValue.toLowerCase());
    }

    isSelectable(item: ItemBaseType<ItemType>): boolean {
        return isSelectable(item) && this.props.isItemDisabled?.(item) !== true;
    }

    onSelectItem(item: ItemBaseType<ItemType>, event?: React.KeyboardEvent) {
        if (this.isSelectable(item)) {
            if (this.props.auditEvent) {
                trackEvent(this.props.auditEvent, { ...item, ...this.context });
            }
            this.props.onSelectItem(item, event);
        }
    }

    focusItem({
        direction = focusItemDirection.first,
        options = this.state.filteredItems,
        tryToSkipAddRow,
    }: {
        direction?: focusItemDirection;
        options?: ItemBaseType<ItemType>[];
        /** only for {@link focusItemDirection.first} */
        tryToSkipAddRow?: boolean;
    }) {
        const { pageSize } = this.props;

        const selectableOptions = options.filter(o => this.isSelectable(o));
        if (!selectableOptions.length) {
            return;
        }

        let nextFocus = 0; // handles 'first'
        let firstIndex = selectableOptions.findIndex(x => !x.addRow);

        if (tryToSkipAddRow && firstIndex > 0) {
            nextFocus = firstIndex;
        }

        const { focusedIndex } = getFocused({
            items: selectableOptions,
            focusedItem: this.state.focusedItem,
            getItemKey: this.props.getItemKey,
        });

        switch (direction) {
            case focusItemDirection.up:
                nextFocus = focusedIndex > 0 ? focusedIndex - 1 : selectableOptions.length - 1;
                break;
            case focusItemDirection.down:
                nextFocus = (focusedIndex + 1) % selectableOptions.length;
                break;
            case focusItemDirection.pageup:
                nextFocus = focusedIndex - pageSize;
                if (nextFocus < 0) {
                    nextFocus = 0;
                }
                break;
            case focusItemDirection.pagedown:
                nextFocus = focusedIndex + pageSize;
                if (nextFocus > selectableOptions.length - 1) {
                    nextFocus = selectableOptions.length - 1;
                }
                break;
            case focusItemDirection.last:
                nextFocus = selectableOptions.length - 1;
                break;
        }
        this.setState({ focusedItem: selectableOptions[nextFocus] });
    }

    onKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case Keys.ArrowUp:
                this.focusItem({ direction: focusItemDirection.up });
                break;
            case Keys.ArrowDown:
                this.focusItem({ direction: focusItemDirection.down });
                break;
            case Keys.PageUp:
                this.focusItem({ direction: focusItemDirection.pageup });
                break;
            case Keys.PageDown:
                this.focusItem({ direction: focusItemDirection.pagedown });
                break;
            case Keys.Home:
                if (this.props.ignoreHomeEndNavigation) {
                    return;
                }
                this.focusItem({ direction: focusItemDirection.first });
                break;
            case Keys.End:
                if (this.props.ignoreHomeEndNavigation) {
                    return;
                }
                this.focusItem({ direction: focusItemDirection.last });
                break;
            case Keys.Enter:
                if ((event.target as HTMLElement).getAttribute("role") === Button.Role) {
                    // treat as an action within the current row
                    return;
                }

                const { filteredItems } = this.state;
                const { focusedItem } = getFocused({
                    items: filteredItems,
                    focusedItem: this.state.focusedItem,
                    getItemKey: this.props.getItemKey,
                });

                if (!focusedItem) {
                    return;
                }

                this.onSelectItem(focusedItem, event);
                break;
            default:
                return;
        }

        // prevent default behavior for handled keys, e.g. home/end, up/down etc.
        event.preventDefault();
    }

    setFocusedItem(item: ItemBaseType<ItemType>) {
        if (this.state.focusedItem !== item && this.isSelectable(item)) {
            this.setState({ focusedItem: item });
        }
    }

    render() {
        const { isLoading, customScrollbar, withAutoRowItemHeight } = this.props;

        let content;

        if (isLoading) {
            content = <Loading withPaddings={true} />;
        } else {
            const hasItems = this.state.filteredItems && this.state.filteredItems.length > 0;
            if (hasItems) {
                const {
                    isVirtualized,
                    getItemLabel,
                    getItemType,
                    itemHeight,
                    listRowComponent,
                    pageSize,
                    listMaxWidth,
                    listWidth,
                    searchText,
                    items,
                    customScrollbarWrapperClassName,
                    getItemKeyForRender,
                    getItemKey,
                    getItemHeight,
                    getItemSelected,
                    isExactMatch,
                    isCreating,
                    tabIndex,
                    getItemClassName,
                    isItemDisabled,
                    disabledItemTooltip,
                    ...restProps
                } = this.props;

                const { filteredItems } = this.state;
                const { focusedItem } = getFocused({
                    items: filteredItems,
                    focusedItem: this.state.focusedItem,
                    getItemKey: this.props.getItemKey,
                });

                const listHeight = itemHeight * (pageSize + 1);
                const listPropsSet = {
                    items: filteredItems,
                    getItemHeight,
                    focusedItem,
                    onSelect: this.onSelectItem,
                    setFocusedItem: this.setFocusedItem,
                    getItemLabel,
                    getItemType,
                    height: listHeight,
                    itemHeight,
                    listRowComponent,
                    searchText,
                    listMaxWidth,
                    listWidth,
                    customScrollbar,
                    customScrollbarWrapperClassName,
                    getItemKeyForRender,
                    getItemSelected,
                    isCreating,
                    getItemClassName,
                    isItemDisabled,
                    disabledItemTooltip,
                };

                const onKeyDown = tabIndex !== undefined ? this.onKeyDown : undefined;

                content = (
                    <div className={blockForFullStory(null, this.props)} tabIndex={tabIndex} onKeyDown={onKeyDown}>
                        {isVirtualized ? (
                            <VirtList {...listPropsSet} {...restProps} ref={this.virtListRef} />
                        ) : (
                            <PlainList {...listPropsSet} withAutoRowItemHeight={withAutoRowItemHeight} />
                        )}
                    </div>
                );
            } else {
                if (this.props.hideNoItem) {
                    return null;
                }

                content = (
                    <NoItemsMessage
                        text={this.props.noItemsText}
                        className={this.props.noItemsClassName}
                        width={this.props.listWidth}
                    />
                );
            }
        }

        return content;
    }

    componentDidUpdate(prevProps: ListProps<ItemType>) {
        const { items, searchText, hideItem, isExactMatch, additionalRows } = this.props;

        const filteredItems = this.getFilteredItemsMemo(items, searchText, hideItem, additionalRows, isExactMatch);

        if (filteredItems !== this.state.filteredItems) {
            this.setState({ filteredItems });
            if (prevProps.searchText !== searchText) {
                this.setupDefaultFocusedItemOnSearch(filteredItems);
            }

            // for VirtList to render with new props to get correct scrollToIndex value
            this._recomputeStyles = true;
            return;
        }

        if (this._recomputeStyles) {
            this._recomputeStyles = false;
            this.virtListRef.current?.invalidateRowsHeight();
        }
    }

    setupDefaultFocusedItemOnSearch(filteredItems: ItemType[]) {
        const { selectedValue } = this.props;

        if (filteredItems.some(x => x === selectedValue)) {
            this.setFocusedItem(selectedValue);
            return;
        }

        this.focusItem({ options: filteredItems, tryToSkipAddRow: true });
    }
}

List.contextType = AuditContext;
